import { Component } from "@angular/core";
import { ColDef, GridOptions } from "ag-grid-community";

import { CaseContextRecordPanelBase } from "@app/case-context/components/case-context-record-panel-base.directive";
import { CaseContextRecordGridComponent } from "@app/case-context/components/case-context-record-grid/case-context-record-grid.component";
import {
  labelValueFormatter,
  recordDateValueFormatter,
  recordCodeValueGetter,
  recordLabelValueGetter,
} from "@app/case-context/components/case-context-record-grid/case-context-record-grid-columns";
import { InmateOffenseRecord } from "@app/case-context/models/inmate-offense-record.model";
import { PHMS_CASE_CONTEXT_TERMINOLOGY } from "@app/phms/phms-case-context.terminology";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";

const DATE_CELL_CLASS = "case-context-record-grid__cell--date";
const STATUS_CELL_CLASS = "case-context-record-grid__cell--status";

/**
 * Programming section of the PHMS incarcerated-person profile (story 936). Renders the neutral
 * programming / program-assignment rows from the shared case-context record surface; accepts rows
 * tagged "programming" or "program_assignment". Empty/loading/error are handled by the shared
 * EZ-grid wrapper so the section never hides the rest of the profile.
 */
@Component({
  selector: "phms-case-context-programming-panel",
  standalone: true,
  imports: [CaseContextRecordGridComponent, TerminologyIdentityDirective],
  templateUrl: "./case-context-programming-panel.component.html",
})
export class CaseContextProgrammingPanelComponent extends CaseContextRecordPanelBase {
  readonly sectionUsage = PHMS_CASE_CONTEXT_TERMINOLOGY.programming;
  readonly sectionText = createTerminologyUsageSignals(this.sectionUsage);

  readonly columnDefs: ColDef<InmateOffenseRecord>[] = [
    {
      field: "eventDate",
      headerName: "Date",
      width: 88,
      minWidth: 84,
      maxWidth: 98,
      sort: "desc",
      cellClass: DATE_CELL_CLASS,
      valueFormatter: recordDateValueFormatter,
    },
    {
      field: "recordCode",
      headerName: "Program code",
      width: 130,
      minWidth: 115,
      valueGetter: recordCodeValueGetter,
    },
    {
      field: "recordLabel",
      headerName: "Program",
      flex: 1,
      minWidth: 220,
      valueGetter: recordLabelValueGetter,
    },
    {
      field: "dispositionCode",
      headerName: "Program Status",
      width: 108,
      minWidth: 96,
      maxWidth: 126,
      cellClass: STATUS_CELL_CLASS,
      valueFormatter: labelValueFormatter,
    },
    {
      field: "recordStatusCode",
      headerName: "Record Status",
      width: 96,
      minWidth: 86,
      maxWidth: 112,
      cellClass: STATUS_CELL_CLASS,
      valueFormatter: labelValueFormatter,
    },
  ];

  readonly agGridOptions: GridOptions = {
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    enableCellTextSelection: true,
  };

  protected accepts(record: InmateOffenseRecord): boolean {
    return record.recordCategoryCode === "programming" || record.recordCategoryCode === "program_assignment";
  }
}
